import React, { useState } from 'react';
import { Edit, Trash2, X, TrendingUp, TrendingDown } from 'lucide-react';
import InputGroup from './InputGroup';

const OperationsView = ({ formatCurrency, operations, setOperations, currentPrices, usdClpRate }) => {
  const [formData, setFormData] = useState({
    date: '',
    assetType: 'ETF',
    asset: '',
    quantity: '',
    price: '',
    type: 'Buy',
    currency: 'CLP'
  });
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    assetType: 'ETF',
    asset: '',
    quantity: '',
    price: '',
    type: 'Buy',
    currency: 'CLP'
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateTotal = (quantity = formData.quantity, price = formData.price) => {
    const qty = parseFloat(quantity) || 0;
    const prc = parseFloat(price) || 0;
    return qty * prc;
  };

  const formatCLP = (val) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(val);

  const formatUSD = (val) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

  const formatByCurrency = (val, curr) => curr === 'USD' ? formatUSD(val) : formatCLP(val);

  const CURRENCY_CHOICE_TYPES = ['FCI', 'Time Deposit', 'ETF', 'Stock'];

  const isUsdPurchase = formData.assetType === 'USD Purchase';
  const isEditUsdPurchase = editFormData.assetType === 'USD Purchase';
  const hasCurrencyChoice = CURRENCY_CHOICE_TYPES.includes(formData.assetType);
  const isEditHasCurrencyChoice = CURRENCY_CHOICE_TYPES.includes(editFormData.assetType);

  const handleSubmit = (e) => {
    e.preventDefault();

    const assetName = isUsdPurchase ? 'USD' : formData.asset;
    if (!formData.date || (!isUsdPurchase && !formData.asset) || !formData.quantity || !formData.price) {
      alert('Por favor complete todos los campos');
      return;
    }

    const newOperation = {
      id: Date.now(),
      name: assetName,
      date: formData.date,
      type: formData.type,
      amount: formData.type === 'Buy'
        ? `+${isUsdPurchase ? formatCLP(calculateTotal()) : hasCurrencyChoice ? formatByCurrency(calculateTotal(), formData.currency) : formatCurrency(calculateTotal())}`
        : `-${isUsdPurchase ? formatCLP(calculateTotal()) : hasCurrencyChoice ? formatByCurrency(calculateTotal(), formData.currency) : formatCurrency(calculateTotal())}`,
      exchangeRate: isUsdPurchase ? parseFloat(formData.price) : undefined,
      operationCurrency: hasCurrencyChoice ? formData.currency : undefined,
      purchaseRate: (hasCurrencyChoice && formData.currency === 'USD') ? usdClpRate : undefined,
      status: 'Completed',
      danger: formData.type === 'Sell',
      assetType: formData.assetType,
      quantity: formData.quantity,
      price: formData.price
    };

    setOperations(prev => [newOperation, ...prev]);

    // Reset form
    setFormData({
      date: '',
      assetType: 'ETF',
      asset: '',
      quantity: '',
      price: '',
      type: 'Buy',
      currency: 'CLP'
    });
  };

  const handleEdit = (operation) => {
    setEditingId(operation.id);
    setEditFormData({
      date: operation.date.split('/').reverse().join('-'), // Convert back to YYYY-MM-DD
      assetType: operation.assetType || 'ETF',
      asset: operation.name,
      quantity: operation.quantity || '',
      price: operation.price || '',
      type: operation.type,
      currency: operation.operationCurrency || 'CLP'
    });
  };

  const handleUpdate = (e) => {
    e.preventDefault();

    const editAssetName = isEditUsdPurchase ? 'USD' : editFormData.asset;
    if (!editFormData.date || (!isEditUsdPurchase && !editFormData.asset) || !editFormData.quantity || !editFormData.price) {
      alert('Por favor complete todos los campos');
      return;
    }

    setOperations(prev => prev.map(op => {
      if (op.id === editingId) {
        return {
          ...op,
          name: editAssetName,
          date: editFormData.date,
          type: editFormData.type,
          amount: editFormData.type === 'Buy'
            ? `+${isEditUsdPurchase ? formatCLP(calculateTotal(editFormData.quantity, editFormData.price)) : isEditHasCurrencyChoice ? formatByCurrency(calculateTotal(editFormData.quantity, editFormData.price), editFormData.currency) : formatCurrency(calculateTotal(editFormData.quantity, editFormData.price))}`
            : `-${isEditUsdPurchase ? formatCLP(calculateTotal(editFormData.quantity, editFormData.price)) : isEditHasCurrencyChoice ? formatByCurrency(calculateTotal(editFormData.quantity, editFormData.price), editFormData.currency) : formatCurrency(calculateTotal(editFormData.quantity, editFormData.price))}`,
          exchangeRate: isEditUsdPurchase ? parseFloat(editFormData.price) : undefined,
          operationCurrency: isEditHasCurrencyChoice ? editFormData.currency : undefined,
          purchaseRate: (isEditHasCurrencyChoice && editFormData.currency === 'USD') ? usdClpRate : op.purchaseRate,
          danger: editFormData.type === 'Sell',
          assetType: editFormData.assetType,
          quantity: editFormData.quantity,
          price: editFormData.price
        };
      }
      return op;
    }));

    setEditingId(null);
    setEditFormData({
      date: '',
      assetType: 'ETF',
      asset: '',
      quantity: '',
      price: '',
      type: 'Buy',
      currency: 'CLP'
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta operación?')) {
      setOperations(prev => prev.filter(op => op.id !== id));
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditFormData({
      date: '',
      assetType: 'ETF',
      asset: '',
      quantity: '',
      price: '',
      type: 'Buy',
      currency: 'CLP'
    });
  };

  return (
    <div className="grid grid-cols-12 gap-8 max-w-7xl mx-auto">
      <div className="col-span-12">
        <h1 className="text-3xl font-bold text-[#041627] mb-2 font-headline">Registro de Operaciones</h1>
        <p className="text-sm text-slate-500 mb-8 uppercase tracking-widest font-bold">Operations Center</p>
      </div>

      <div className="col-span-12 lg:col-span-5 bg-white rounded-xl p-5 md:p-8 shadow-sm border border-slate-100">
        <h3 className="font-bold text-xl mb-6 text-[#041627]">Nueva Transacción</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <InputGroup
              label="Fecha"
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              required
            />
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-[#44474c]">Tipo de Activo</label>
              <select
                value={formData.assetType}
                onChange={(e) => handleInputChange('assetType', e.target.value)}
                className="bg-[#f2f4f7] border-none rounded-lg p-2.5 text-sm"
              >
                <option>ETF</option>
                <option>Stock</option>
                <option>FCI</option>
                <option>Time Deposit</option>
                <option>USD Purchase</option>
                <option>Liquidity</option>
              </select>
            </div>
          </div>
          {hasCurrencyChoice && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-[#44474c]">Moneda</label>
              <div className="flex gap-2">
                {['CLP', 'USD'].map(cur => (
                  <button
                    key={cur}
                    type="button"
                    onClick={() => handleInputChange('currency', cur)}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-all ${
                      formData.currency === cur
                        ? 'bg-[#041627] text-white border-[#041627]'
                        : 'bg-[#f2f4f7] text-[#44474c] border-transparent hover:border-slate-300'
                    }`}
                  >
                    {cur}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!isUsdPurchase && (
            <InputGroup
              label="Activo / Ticker"
              placeholder="ej. VOO, NVDA"
              value={formData.asset}
              onChange={(e) => handleInputChange('asset', e.target.value)}
              required
            />
          )}
          <div className="grid grid-cols-2 gap-4">
            <InputGroup
              label={isUsdPurchase ? 'Cantidad (USD)' : 'Cantidad'}
              type="number"
              placeholder="0.00"
              step="0.01"
              value={formData.quantity}
              onChange={(e) => handleInputChange('quantity', e.target.value)}
              required
            />
            <InputGroup
              label={isUsdPurchase ? 'Tipo de Cambio (CLP/USD)' : (hasCurrencyChoice && formData.currency === 'USD' ? 'Precio (USD)' : 'Precio')}
              type="number"
              placeholder={isUsdPurchase ? 'ej. 940' : '0.00'}
              step="0.01"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-[#44474c]">Tipo de Operación</label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className="bg-[#f2f4f7] border-none rounded-lg p-2.5 text-sm"
            >
              <option>Buy</option>
              <option>Sell</option>
            </select>
          </div>
          <div className="bg-[#041627] text-white p-4 rounded-xl flex justify-between items-center">
            <div>
              <p className="text-[10px] uppercase font-bold opacity-60">
                {isUsdPurchase ? 'Total en CLP' : hasCurrencyChoice ? `Total en ${formData.currency}` : 'Total Calculado'}
              </p>
              <p className="text-xl font-bold">
                {isUsdPurchase
                  ? formatCLP(calculateTotal())
                  : hasCurrencyChoice
                  ? formatByCurrency(calculateTotal(), formData.currency)
                  : formatCurrency(calculateTotal())}
              </p>
              {isUsdPurchase && parseFloat(formData.quantity) > 0 && (
                <p className="text-[11px] opacity-70 mt-0.5">
                  {parseFloat(formData.quantity).toLocaleString('es-CL')} USD × {parseFloat(formData.price || 0).toLocaleString('es-CL')} CLP
                </p>
              )}
            </div>
            <button type="submit" className="bg-[#6bfe9c] text-[#005228] px-4 py-2 rounded-lg font-bold text-xs hover:opacity-90">
              Ejecutar
            </button>
          </div>
        </form>
      </div>

      <div className="col-span-12 lg:col-span-7 space-y-4">
        <h3 className="font-bold text-xl text-[#041627]">Actividad Reciente</h3>
        {operations.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-slate-100">
            <p className="text-slate-500">No hay operaciones registradas aún</p>
            <p className="text-sm text-slate-400 mt-2">Registra tu primera transacción usando el formulario</p>
          </div>
        ) : (
          operations.map(operation => (
            <div key={operation.id} className="bg-white p-4 rounded-xl border border-slate-100 hover:shadow-md transition-all relative">
              <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${operation.danger ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                    {operation.danger ? <TrendingDown size={18} /> : <TrendingUp size={18} />}
                  </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#041627] truncate">{operation.name}</p>
                    <p className="text-[10px] text-slate-400 font-medium">{operation.date} • {operation.type}</p>
                  </div>
                </div>
                <div className="text-right flex items-center gap-2 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#041627]">{operation.amount}</p>
                    {operation.assetType === 'USD Purchase' && operation.quantity && operation.exchangeRate && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {parseFloat(operation.quantity).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} USD
                        {' · '}
                        TC {parseFloat(operation.exchangeRate).toLocaleString('es-CL')}
                      </p>
                    )}
                    {(operation.assetType === 'ETF' || operation.assetType === 'Stock') && operation.quantity && operation.price && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {parseFloat(operation.quantity).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 4 })} shares
                        {' · '}
                        {operation.operationCurrency === 'USD' ? formatUSD(parseFloat(operation.price)) : formatCLP(parseFloat(operation.price))} c/u
                        {' · '}{operation.operationCurrency === 'USD' ? 'USD' : 'CLP'}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(operation)}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(operation.id)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit Modal */}
      {editingId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-[#041627]">Editar Operación</h3>
              <button
                onClick={cancelEdit}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#44474c]">Fecha</label>
                  <input
                    type="date"
                    value={editFormData.date}
                    onChange={(e) => handleEditInputChange('date', e.target.value)}
                    className="bg-[#f2f4f7] border-none rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#041627]/10"
                    required
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#44474c]">Tipo de Activo</label>
                  <select
                    value={editFormData.assetType}
                    onChange={(e) => handleEditInputChange('assetType', e.target.value)}
                    className="bg-[#f2f4f7] border-none rounded-lg p-2.5 text-sm"
                  >
                    <option>ETF</option>
                    <option>Stock</option>
                    <option>FCI</option>
                    <option>Time Deposit</option>
                    <option>USD Purchase</option>
                    <option>Liquidity</option>
                  </select>
                </div>
              </div>
              {isEditHasCurrencyChoice && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#44474c]">Moneda</label>
                  <div className="flex gap-2">
                    {['CLP', 'USD'].map(cur => (
                      <button
                        key={cur}
                        type="button"
                        onClick={() => handleEditInputChange('currency', cur)}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-all ${
                          editFormData.currency === cur
                            ? 'bg-[#041627] text-white border-[#041627]'
                            : 'bg-[#f2f4f7] text-[#44474c] border-transparent hover:border-slate-300'
                        }`}
                      >
                        {cur}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {!isEditUsdPurchase && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#44474c]">Activo / Ticker</label>
                  <input
                    type="text"
                    placeholder="ej. VOO, NVDA"
                    value={editFormData.asset}
                    onChange={(e) => handleEditInputChange('asset', e.target.value)}
                    className="bg-[#f2f4f7] border-none rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#041627]/10"
                    required
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#44474c]">{isEditUsdPurchase ? 'Cantidad (USD)' : 'Cantidad'}</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    step="0.01"
                    value={editFormData.quantity}
                    onChange={(e) => handleEditInputChange('quantity', e.target.value)}
                    className="bg-[#f2f4f7] border-none rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#041627]/10"
                    required
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#44474c]">{isEditUsdPurchase ? 'Tipo de Cambio (CLP/USD)' : (isEditHasCurrencyChoice && editFormData.currency === 'USD' ? 'Precio (USD)' : 'Precio')}</label>
                  <input
                    type="number"
                    placeholder={isEditUsdPurchase ? 'ej. 940' : '0.00'}
                    step="0.01"
                    value={editFormData.price}
                    onChange={(e) => handleEditInputChange('price', e.target.value)}
                    className="bg-[#f2f4f7] border-none rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#041627]/10"
                    required
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-[#44474c]">Tipo de Operación</label>
                <select
                  value={editFormData.type}
                  onChange={(e) => handleEditInputChange('type', e.target.value)}
                  className="bg-[#f2f4f7] border-none rounded-lg p-2.5 text-sm"
                >
                  <option>Buy</option>
                  <option>Sell</option>
                </select>
              </div>
              <div className="bg-[#041627] text-white p-4 rounded-xl flex justify-between items-center">
                <div>
                  <p className="text-[10px] uppercase font-bold opacity-60">
                    {isEditUsdPurchase ? 'Total en CLP' : isEditHasCurrencyChoice ? `Total en ${editFormData.currency}` : 'Total Calculado'}
                  </p>
                  <p className="text-xl font-bold">
                    {isEditUsdPurchase
                      ? formatCLP(calculateTotal(editFormData.quantity, editFormData.price))
                      : isEditHasCurrencyChoice
                      ? formatByCurrency(calculateTotal(editFormData.quantity, editFormData.price), editFormData.currency)
                      : formatCurrency(calculateTotal(editFormData.quantity, editFormData.price))}
                  </p>
                  {isEditUsdPurchase && parseFloat(editFormData.quantity) > 0 && (
                    <p className="text-[11px] opacity-70 mt-0.5">
                      {parseFloat(editFormData.quantity).toLocaleString('es-CL')} USD × {parseFloat(editFormData.price || 0).toLocaleString('es-CL')} CLP
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="bg-slate-600 text-white px-4 py-2 rounded-lg font-bold text-xs hover:opacity-90"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="bg-[#6bfe9c] text-[#005228] px-4 py-2 rounded-lg font-bold text-xs hover:opacity-90">
                    Actualizar
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationsView;